import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import type { Cart, FarmProduct } from '@/types/cart';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface PendingAdd {
  product: FarmProduct;
  producerId: string;
  producerName: string;
}

interface FarmCartContextValue {
  cart: Cart | null;
  itemCount: number;
  totalPrice: number;
  addToCart: (product: FarmProduct, producerId: string, producerName: string) => void;
  removeFromCart: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  isCartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
}

const FarmCartContext = createContext<FarmCartContextValue | null>(null);

export function FarmCartProvider({ children }: { children: React.ReactNode }) {
  const [cart, setCart] = useState<Cart | null>(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  
  // State for producer switch confirmation modal
  const [pendingAdd, setPendingAdd] = useState<PendingAdd | null>(null);

  const addToCart = useCallback((product: FarmProduct, producerId: string, producerName: string) => {
    setCart(prevCart => {
      // If cart is for a different producer, show confirmation modal instead of auto-switching
      if (prevCart && prevCart.producerId !== producerId) {
        setPendingAdd({ product, producerId, producerName });
        return prevCart; // Don't change cart yet
      }

      // If no cart exists, create one
      if (!prevCart) {
        return {
          producerId,
          producerName,
          items: [{ product, quantity: 1 }],
        };
      }

      // Check if product already in cart
      const existingItem = prevCart.items.find(item => item.product.id === product.id);
      
      if (existingItem) {
        // Increase quantity
        return {
          ...prevCart,
          items: prevCart.items.map(item =>
            item.product.id === product.id
              ? { ...item, quantity: item.quantity + 1 }
              : item
          ),
        };
      }

      // Add new item
      return {
        ...prevCart,
        items: [...prevCart.items, { product, quantity: 1 }],
      };
    });
  }, []);

  const handleConfirmSwitch = useCallback(() => {
    if (!pendingAdd) return;
    
    // Clear cart and add new item from new producer
    setCart({
      producerId: pendingAdd.producerId,
      producerName: pendingAdd.producerName,
      items: [{ product: pendingAdd.product, quantity: 1 }],
    });
    setPendingAdd(null);
  }, [pendingAdd]);

  const handleCancelSwitch = useCallback(() => {
    setPendingAdd(null);
  }, []);

  const removeFromCart = useCallback((productId: string) => {
    setCart(prevCart => {
      if (!prevCart) return null;

      const newItems = prevCart.items.filter(item => item.product.id !== productId);
      
      if (newItems.length === 0) {
        return null;
      }

      return { ...prevCart, items: newItems };
    });
  }, []);

  const updateQuantity = useCallback((productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }

    setCart(prevCart => {
      if (!prevCart) return null;

      return {
        ...prevCart,
        items: prevCart.items.map(item =>
          item.product.id === productId
            ? { ...item, quantity }
            : item
        ),
      };
    });
  }, [removeFromCart]);

  const clearCart = useCallback(() => {
    setCart(null);
  }, []);

  const openCart = useCallback(() => setIsCartOpen(true), []);
  const closeCart = useCallback(() => setIsCartOpen(false), []);

  const itemCount = useMemo(() => {
    if (!cart) return 0;
    return cart.items.reduce((sum, item) => sum + item.quantity, 0);
  }, [cart]);

  const totalPrice = useMemo(() => {
    if (!cart) return 0;
    return cart.items.reduce(
      (sum, item) => sum + item.product.price * item.quantity,
      0
    );
  }, [cart]);

  const value = useMemo(() => ({
    cart,
    itemCount,
    totalPrice,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    isCartOpen,
    openCart,
    closeCart,
  }), [cart, itemCount, totalPrice, addToCart, removeFromCart, updateQuantity, clearCart, isCartOpen, openCart, closeCart]);

  return (
    <FarmCartContext.Provider value={value}>
      {children}
      
      {/* Producer switch confirmation dialog */}
      <AlertDialog open={!!pendingAdd} onOpenChange={(open) => !open && handleCancelSwitch()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Byta producent?</AlertDialogTitle>
            <AlertDialogDescription>
              Du har redan varor från <strong>{cart?.producerName}</strong> i varukorgen. 
              Vill du tömma varukorgen och byta till <strong>{pendingAdd?.producerName}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelSwitch}>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSwitch}>Töm och byt</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FarmCartContext.Provider>
  );
}

export function useFarmCart() {
  const context = useContext(FarmCartContext);
  if (!context) {
    throw new Error('useFarmCart must be used within a FarmCartProvider');
  }
  return context;
}
